import { useState } from "react";
import { useTranslations } from "@/contexts/I18nContext";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { DownloadableImage } from "@/components/ui/DownloadableImage";
import { generateGroceryListFromCookbook } from "@/lib/cookbookActions";
import type { CookbookMeal } from "@/lib/models";

interface CookbookRecipeDetailSheetProps {
  meal: CookbookMeal;
  onClose: () => void;
}

/**
 * Member-facing recipe detail sheet. Mirrors iOS
 * `CookbookRecipeDetailView.swift` density — hero image with optional
 * attribution caption, name + meta, meal-type pill, dietary chips,
 * ingredient list, numbered instructions. Plus a sticky bottom
 * action row with a servings stepper + "Add to grocery list" CTA
 * that invokes the existing `generateGroceryListFromCookbook`
 * callable Cloud Function (the same one iOS calls from
 * `CookbookRepository.generateGroceryList`).
 */
export function CookbookRecipeDetailSheet({
  meal,
  onClose,
}: CookbookRecipeDetailSheetProps) {
  const t = useTranslations();
  const { householdId } = useHouseholdContext();

  const heroUrl = meal.hero_image_url ?? null;
  const attribution = meal.hero_image_attribution ?? null;
  const dietaryTags = meal.dietary_tags ?? [];
  const ingredients = meal.ingredients ?? [];
  const instructions = meal.instructions ?? [];
  const prepTime = meal.prep_time_minutes ?? 0;
  const baseServings = meal.servings ?? 0;
  const calories = meal.calories_per_serving;
  const mealType = meal.meal_type;

  const [servings, setServings] = useState(Math.max(1, baseServings || 4));
  const [adding, setAdding] = useState(false);
  const [feedback, setFeedback] = useState<{
    kind: "ok" | "error";
    text: string;
  } | null>(null);

  const handleAddToGroceryList = async () => {
    if (!householdId || !meal.uid || adding) return;
    setAdding(true);
    setFeedback(null);
    try {
      const result = await generateGroceryListFromCookbook({
        householdId,
        selections: [{ mealId: meal.uid, servings }],
      });
      const added = result.added ?? result.ingredients ?? 0;
      setFeedback({
        kind: "ok",
        text: t("cookbook.detail.addedCount", { count: String(added) }),
      });
    } catch (err) {
      console.error("generateGroceryListFromCookbook failed:", err);
      setFeedback({
        kind: "error",
        text: t("cookbook.detail.addError"),
      });
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full sm:max-w-lg bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden text-gray-900 dark:text-gray-100 max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-100 dark:border-white/10 shrink-0">
          <h2 className="font-bold text-base">
            {t("cookbook.detail.title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="text-sm font-semibold text-[color:var(--accent-color)]"
          >
            {t("cookbook.detail.done")}
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto">
          {/* Hero */}
          <div className="relative w-full bg-gray-100 dark:bg-gray-800">
            {heroUrl ? (
              <div className="aspect-[16/9] w-full overflow-hidden">
                <DownloadableImage
                  src={heroUrl}
                  alt={meal.name}
                  filename={`${meal.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.jpg`}
                />
              </div>
            ) : (
              <div className="aspect-[16/9] w-full flex items-center justify-center text-4xl text-gray-300 dark:text-gray-500">
                {"\u{1F374}"}
              </div>
            )}
            {attribution && (
              <p className="px-4 py-1.5 text-[11px] text-gray-500 dark:text-gray-400 italic">
                {attribution}
              </p>
            )}
          </div>

          {/* Title + meta */}
          <div className="p-4 space-y-2">
            <h3 className="text-xl font-bold">{meal.name}</h3>
            {mealType && (
              <span className="inline-block text-[11px] px-2 py-0.5 rounded-full bg-[color:var(--accent-color)]/15 text-[color:var(--accent-color)] capitalize">
                {mealType.replace(/_/g, " ")}
              </span>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              {prepTime > 0 && (
                <span>
                  {t("cookbook.detail.prepMinutes", { minutes: String(prepTime) })}
                </span>
              )}
              {servings > 0 && (
                <span>
                  {t("cookbook.detail.servingsCount", { count: String(servings) })}
                </span>
              )}
              {calories != null && calories > 0 && (
                <span>
                  {t("cookbook.detail.caloriesPerServing", { calories: String(calories) })}
                </span>
              )}
            </div>

            {meal.description && (
              <p className="text-sm text-gray-600 dark:text-gray-300 pt-1">
                {meal.description}
              </p>
            )}

            {dietaryTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {dietaryTags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-300 capitalize"
                  >
                    {tag.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Ingredients */}
          {ingredients.length > 0 && (
            <div className="px-4 pb-4">
              <h4 className="font-semibold text-sm mb-2">
                {t("cookbook.detail.ingredients")}
              </h4>
              <ul className="space-y-1">
                {ingredients.map((ing, idx) => {
                  const qty =
                    ing.quantity === Math.floor(ing.quantity) && ing.quantity < 100
                      ? String(Math.floor(ing.quantity))
                      : ing.quantity.toFixed(2);
                  return (
                    <li
                      key={`${ing.name}-${idx}`}
                      className="text-sm text-gray-700 dark:text-gray-300 flex gap-2"
                    >
                      <span className="text-gray-400">•</span>
                      <span className="flex-1">
                        {qty} {ing.unit} {ing.name}
                        {ing.optional && (
                          <span className="text-[11px] text-gray-400 ml-1">
                            ({t("cookbook.detail.optional")})
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Instructions */}
          {instructions.length > 0 && (
            <div className="px-4 pb-6">
              <h4 className="font-semibold text-sm mb-2">
                {t("cookbook.detail.instructions")}
              </h4>
              <ol className="space-y-2">
                {instructions.map((step, idx) => (
                  <li
                    key={idx}
                    className="text-sm text-gray-700 dark:text-gray-300 flex gap-3"
                  >
                    <span className="font-semibold text-[color:var(--accent-color)] shrink-0 w-6">
                      {idx + 1}.
                    </span>
                    <span className="flex-1">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {ingredients.length === 0 && instructions.length === 0 && (
            <div className="px-4 pb-6">
              <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                {t("cookbook.detail.noContent")}
              </p>
            </div>
          )}
        </div>

        {/* Sticky bottom action row — servings stepper + add-to-grocery
            CTA. Mirrors iOS `CookbookRecipeDetailView.swift:200-236`. */}
        <div className="border-t border-gray-100 dark:border-white/10 p-3 bg-white dark:bg-gray-900 shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              {t("cookbook.detail.servingsLabel")}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setServings((v) => Math.max(1, v - 1))}
                disabled={adding || servings <= 1}
                className="w-8 h-8 rounded-full bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-200 font-bold disabled:opacity-40"
                aria-label={t("cookbook.detail.servingsDecrease")}
              >
                −
              </button>
              <span className="min-w-[2rem] text-center text-sm font-semibold">
                {servings}
              </span>
              <button
                type="button"
                onClick={() => setServings((v) => Math.min(12, v + 1))}
                disabled={adding || servings >= 12}
                className="w-8 h-8 rounded-full bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-200 font-bold disabled:opacity-40"
                aria-label={t("cookbook.detail.servingsIncrease")}
              >
                +
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={handleAddToGroceryList}
            disabled={adding || !householdId || !meal.uid}
            className="w-full py-2.5 rounded-lg text-sm font-semibold text-white bg-[color:var(--accent-color)] disabled:opacity-50 transition-opacity"
          >
            {adding
              ? t("cookbook.detail.adding")
              : t("cookbook.detail.addToGroceryList")}
          </button>
          {feedback && (
            <p
              className={`text-xs mt-2 ${
                feedback.kind === "ok"
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-red-500"
              }`}
            >
              {feedback.text}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
